import {
  AssetDTO,
  BaseEntityDTO,
  BranchYearDTO,
  ContestDTO,
  IAssetDTO,
  IBaseEntityDTO,
  IBranchDTO,
  IBranchYearDTO,
  IContestDTO,
  INoteDTO,
  IOrderDTO,
  IProductVariantDTO,
  ISubmissionDTO,
  ITeamCriteriaSelectionDTO,
  ITeamDTO,
  ITeamLevelDTO,
  NoteDTO,
  OrderDTO,
  SubmissionDTO,
  TeamCriteriaSelectionDTO,
  TeamDTO,
  TeamLevelDTO,
} from './';

export interface ITeamCriteriaDTO extends IBaseEntityDTO {
  team?: ITeamDTO;
  criteriaRelations: ITeamCriteriaSelectionDTO[];
  branchYear?: IBranchYearDTO;
  teamLevel?: ITeamLevelDTO;
  registered_date?: Date;
  checked_in: boolean;
  checked_in_date?: Date;
  terms?: IAssetDTO;
  order_number: string;
  sku: string;
  is_inperson: boolean;
  notes: INoteDTO[];
  submissions: ISubmissionDTO[];
  contests: IContestDTO[]; // Array of contests
  friday_block: number;
  friday_order: number;
  saturday_block: number;
  saturday_order: number;
  order?: IOrderDTO;
  validated: boolean;
  validationType: 'EMAIL' | 'PHOTO' | 'NONE';
  validationEmail: string;
  validationCode: string;
  validationAsset?: IAssetDTO;
  validationDate?: Date;
  registeredBranch(): IBranchDTO | undefined;
  registeredProductVariant(): IProductVariantDTO | undefined;
  hasSignedTerms(): boolean;
  isRegistered(): boolean;
  canSelectCriteria(): boolean;
  canSubmitFilm(): boolean;
  showScreeningCopy(): boolean;
  isLate(): boolean;
  passTurnInDeadline(): boolean;
}
export class TeamCriteriaDTO extends BaseEntityDTO implements ITeamCriteriaDTO {
  team?: ITeamDTO;
  criteriaRelations: ITeamCriteriaSelectionDTO[];
  branchYear?: IBranchYearDTO;
  teamLevel?: ITeamLevelDTO;
  registered_date?: Date;
  checked_in: boolean;
  checked_in_date?: Date;
  terms?: IAssetDTO;
  order_number: string;
  sku: string;
  is_inperson: boolean;
  notes: INoteDTO[];
  submissions: ISubmissionDTO[];
  contests: IContestDTO[]; // Array of contests
  friday_block: number;
  friday_order: number;
  saturday_block: number;
  saturday_order: number;
  validated: boolean;
  validationType: 'EMAIL' | 'PHOTO' | 'NONE';
  validationEmail: string;
  validationCode: string;
  validationAsset?: IAssetDTO;
  validationDate?: Date;
  order?: IOrderDTO;

  constructor(data?: Partial<TeamCriteriaDTO>) {
    super(data);
    this.team = data?.team ? new TeamDTO(data.team) : undefined;
    this.branchYear = data?.branchYear ? new BranchYearDTO(data.branchYear) : undefined;
    this.teamLevel = data?.teamLevel ? new TeamLevelDTO(data.teamLevel) : undefined;
    this.registered_date = data?.registered_date ?? undefined;
    this.checked_in = data?.checked_in ?? false;
    this.checked_in_date = data?.checked_in_date ?? undefined;
    this.terms = data?.terms ? new AssetDTO(data.terms) : undefined;
    this.order_number = data?.order_number ?? '';
    this.sku = data?.sku ?? '';
    this.is_inperson = data?.is_inperson ?? true;
    this.friday_block = data?.friday_block ?? 0;
    this.friday_order = data?.friday_order ?? 0;
    this.saturday_block = data?.saturday_block ?? 0;
    this.saturday_order = data?.saturday_order ?? 0;

    this.criteriaRelations = data?.criteriaRelations
      ? data.criteriaRelations.map((critRelation) => new TeamCriteriaSelectionDTO(critRelation))
      : [];
    this.notes = data?.notes ? data.notes.map((note) => new NoteDTO(note)) : [];
    this.submissions = data?.submissions
      ? data.submissions.map((submission) => new SubmissionDTO(submission))
      : [];
    this.contests = data?.contests ? data.contests.map((contest) => new ContestDTO(contest)) : [];
    this.order = new OrderDTO(data?.order);
    this.validated = data?.validated ?? false;
    this.validationType = data?.validationType ?? 'NONE';
    this.validationEmail = data?.validationEmail ?? '';
    this.validationCode = data?.validationCode ?? '';
    this.validationAsset = data?.validationAsset
      ? new AssetDTO(data.validationAsset)
      : new AssetDTO();
    this.validationDate = data?.validationDate ?? undefined;
  }

  registeredBranch(): IBranchDTO | undefined {
    if (this.branchYear && this.branchYear.branch) {
      return this.branchYear.branch;
    } else {
      return undefined;
    }
  }
  registeredProductVariant(): IProductVariantDTO | undefined {
    if (this.order && this.order.productVariant) {
      return this.order.productVariant;
    } else {
      return undefined;
    }
  }
  hasSignedTerms(): boolean {
    if (this.terms && this.terms.id) {
      return true;
    } else {
      return false;
    }
  }
  isRegistered(): boolean {
    return this && this.registered_date ? true : false;
  }
  canSelectCriteria(): boolean {
    let canSelect = false;
    if (this.criteriaRelations.length > 0) {
      canSelect = true;
    } else if (this.branchYear?.team_select === true) {
      const currentDate = new Date();
      const currentUTC = new Date(currentDate.toISOString()); // force UTC
      if (this.branchYear.criteria_selection_start && this.branchYear.criteria_selection_end) {
        const start = new Date(this.branchYear.criteria_selection_start);
        const end = new Date(this.branchYear.criteria_selection_end);
        if (currentUTC >= start && currentUTC <= end) {
          canSelect = true;
        }
      }
    }
    return canSelect;
  }
  canSubmitFilm(): boolean {
    // Is Registered for the year, has picked one or more criteria, and is within the turn in window
    if (
      this.isRegistered() &&
      this.criteriaRelations.length > 0 &&
      this.branchYear?.year?.turn_start &&
      this.branchYear?.year?.screening_copy_deadline
    ) {
      const currentDate = new Date();
      const currentUTC = new Date(currentDate.toISOString()); // force UTC
      const start = new Date(this.branchYear.year.turn_start);
      const end = new Date(this.branchYear.year.screening_copy_deadline);
      return currentUTC >= start && currentUTC <= end;
    }
    return false;
  }

  showScreeningCopy(): boolean {
    if (
      this.branchYear?.year?.turn_deadline &&
      this.branchYear?.year?.screening_copy_deadline &&
      this.submissions[0]?.movie != undefined
    ) {
      const currentDate = new Date();
      const currentUTC = new Date(currentDate.toISOString()); // force UTC
      const end = new Date(this.branchYear.year.turn_deadline);
      const screening = new Date(this.branchYear.year.turn_deadline);
      const movieDate = new Date(this.submissions[0].movie.created_on);
      return currentUTC >= end && movieDate <= end && currentUTC <= screening;
    }
    return false;
  }

  passTurnInDeadline(): boolean {
    if (this.branchYear?.year?.turn_deadline) {
      const currentDate = new Date();
      const currentUTC = new Date(currentDate.toISOString()); // force UTC
      const end = new Date(this.branchYear.year.turn_deadline);
      return currentUTC > end;
    }
    return false;
  }
  isLate(): boolean {
    if (this.branchYear?.year?.turn_deadline && this.submissions[0]?.movie == undefined) {
      const currentDate = new Date();
      const currentUTC = new Date(currentDate.toISOString()); // force UTC
      const end = new Date(this.branchYear.year.turn_deadline);
      return currentUTC >= end;
    }
    return false;
  }
}
